import { Injectable } from '@angular/core';
import {
  OfflinePostCrAndRelevesAndPrimesRequest,
  OfflinePostCrRequest,
  PostCrAndRelevesAndPrimesRequest,
} from '../activites/post-cr.service';
import moment from 'moment/moment';
import { RequestsProvider, TypeDataRequest } from './requests';
import { PostPhotoService } from '../activites/post-photo.service';
import { OfflineSyncProvider } from '../offline-sync/offline-sync';
import { NetworkProvider } from '../network/network';
import { LoggerFactory } from '../logger/logger-factory.service';
import { LoggerWrapper } from '../logger/logger-wrapper.service';
import { StorageProvider } from '../storage/storage';
import { environment } from '../../../../environments/environment';
import { copyPostCrForLog, deepCopy } from '../../utils/deepCopy';
import { UserService } from '../auth/user.service';
import { AngularFireStorage } from '../firebase-storage/firebase-storage.provider';
import { LocalisationResponse } from '../../../api/models/localisation-response';
import { CrReleveCompteurPhotoService } from '../../../mnt/cr/cr-releve-equipements-modal/cr-releve-compteur/cr-releve-compteur-saisie/cr-releve-compteur-photo.service';

@Injectable({ providedIn: 'root' })
export class RequestsOfflineProvider {
  /**
   * Mutex pour le process des requetes
   */
  in_progress = false;
  private logger: LoggerWrapper;
  apiActivitesUrl = '';

  constructor(
    private loggerFactory: LoggerFactory,
    private requestProvider: RequestsProvider,
    private userService: UserService,
    private postDocumentService: PostPhotoService,
    private crReleveCompteurPhotoService: CrReleveCompteurPhotoService,
    private offlineSyncProvider: OfflineSyncProvider,
    private networkProvider: NetworkProvider,
    private storageProvider: StorageProvider,
    private storage: AngularFireStorage
  ) {
    this.logger = this.loggerFactory.getInstance(this, true);
    this.apiActivitesUrl = environment.apis.API_ACTIVITES;
  }

  postOfflinePostCrAndRelevesAndPrimesRequest(
    url: string,
    crAndRelevesAndPrimesRequest: PostCrAndRelevesAndPrimesRequest,
    extraData: { id_ot: string; numero_ot: string; site: LocalisationResponse },
    headers = { 'Content-Type': 'application/json' },
    force_offline = false
  ): Promise<any> {
    this.logger.info('Calling postOfflinePostCrAndRelevesAndPrimesRequest');
    return new Promise(async (resolve, reject) => {
      if (await this.requestProvider.modeOnline(force_offline)) {
        this.logger.debug(
          'Envoi du CR lors du postOfflinePostCrAndRelevesAndPrimesRequest :',
          copyPostCrForLog(crAndRelevesAndPrimesRequest)
        );
        await this.postDocumentService.retryPostPhotoForOt(crAndRelevesAndPrimesRequest.cr);
        await this.crReleveCompteurPhotoService.retryStoredRequests(crAndRelevesAndPrimesRequest.releve);
        this.requestProvider.httpPost(url, crAndRelevesAndPrimesRequest, headers, resolve, reject);
      } else {
        this.logger.info('Le CR a été posté en offline', crAndRelevesAndPrimesRequest.cr.ot.id_ot);
        const offlineData: OfflinePostCrAndRelevesAndPrimesRequest = {
          offline: 'waiting',
          type: TypeDataRequest.CRS_WITH_RELEVES_AND_PRIMES,
          primes: crAndRelevesAndPrimesRequest.primes,
          releve: crAndRelevesAndPrimesRequest.releve,
          cr: {
            ...crAndRelevesAndPrimesRequest.cr,
            id_ot: extraData.id_ot,
            numero_ot: extraData.numero_ot,
            site: extraData.site,
          },
        };

        const alreadyScheduledForOfflinePost =
          await this.requestProvider.checkIfAlreadyScheduledForOfflinePost(offlineData);

        if (alreadyScheduledForOfflinePost) {
          resolve('doublon');
          return;
        }

        const offlineCr: OfflinePostCrRequest = offlineData.cr;
        offlineCr.offline = 'waiting';
        if (!offlineCr.id) {
          offlineCr.id = extraData.id_ot + moment().toISOString();
        }

        this.offlineSyncProvider.add('POST', url, offlineData, headers);
        resolve(crAndRelevesAndPrimesRequest);
      }
      this.logger.info('End postOfflinePostCrAndRelevesAndPrimesRequest');
    });
  }

  // OFFLINE SYNC

  /**
   * Méthode de gestion des requetes stockées hors-ligne
   *
   * Lorsque l'on est connecté, on recupère les requetes en attente et on execute chaque requete une par une.
   */
  async processRequests() {
    return new Promise(async (resolve) => {
      if (this.in_progress) {
        console.log('processRequests STOP in_progress', this.in_progress);
        this.networkProvider.addHistory("File d'attente déjà en cours de traitement");
        return;
      }
      this.in_progress = true;
      this.offlineSyncProvider
        .getRequests()
        .then(async (requests) => {
          if (requests.length > 0) {
            if (!(await this.networkProvider.isReallyConnected())) {
              console.log('processRequests STOP isReallyConnected', false);
              this.networkProvider.addHistory("File d'attente stoppé, pas de connexion suffisante");
              this.in_progress = false;
            } else {
              this.networkProvider.addHistory("Démarrage de la file d'attente (" + requests.length + ' requêtes)');
              await this.loopRequest(requests);
              this.networkProvider.addHistory("Fin du traitement de la file d'attente");
            }
          }
          this.in_progress = false;
          resolve(null);
        })
        .catch((e) => {
          console.log('getRequests err', e);
          this.in_progress = false;
          resolve(null);
        });
    });
  }

  async tryToRefreshOt(idOt: string) {
    try {
      this.logger.info("Renvoie de l'OT " + idOt);
      const ot = await this.requestProvider.get(this.apiActivitesUrl + '/' + idOt);
      await this.storageProvider.set(idOt, ot);
    } catch (e) {
      this.logger.error(e, "Impossible de rafraichir l'OT: " + idOt);
    }
  }

  /**
   * Execution des requetes qui était stockées hors-ligne
   *
   * @param requests les requetes à éxécuter
   */
  async loopRequest(requests: any) {
    let result = deepCopy(requests);
    let i = 0;
    for (let request of requests) {
      this.logger.info('Renvoie de la requete ' + request.url);
      if (request.data.id) delete request.data.id;
      if (request.data.offline) delete request.data.offline;
      if (request.data.draft_id) delete request.data.draft_id;
      let request_type = request.data.type;
      delete request.data.type;

      if (request.method == 'POST') {
        if (request_type && request_type == TypeDataRequest.CRS_WITH_RELEVES_AND_PRIMES) {
          await this.postDocumentService.retryPostPhotoForOt(request.data.cr);
          await this.crReleveCompteurPhotoService.retryStoredRequests(request.data.releve);
        }
        await this.requestProvider
          .post(request.url, request.data)
          .then(async (data) => {
            if (request_type && data['id_cr'] && request_type == TypeDataRequest.CRS_WITH_RELEVES_AND_PRIMES) {
              let data: PostCrAndRelevesAndPrimesRequest = request.data;
              await this.tryToRefreshOt(data.cr.ot.id_ot);
            }
            result.splice(i, 1);
          })
          .catch((err) => {
            this.logger.error(err, "ERREUR lors du renvoi d'une requete POST");
            let jsonString = JSON.stringify({
              method: 'POST',
              url: request.url,
              body: request.data,
              error: err,
            });
            // create a Blob from the JSON-string
            let blob = new Blob([jsonString], { type: 'application/json' });
            let logPathDate = new Date().toJSON().slice(0, 10).replace(/-/g, '/');
            let logPathTime = new Date().toLocaleTimeString().replace(/:/g, '_') + '_' + new Date().getMilliseconds();
            let currentUser = this.userService.backendUser;
            this.storage.upload(
              environment.name +
                '/' +
                logPathDate +
                '/' +
                currentUser.matricule +
                '_offline_post_' +
                logPathTime +
                '.json',
              blob
            );

            if (err.status && (err.status == 409 || err.status == 500 || err.status == 200)) {
              result.splice(i, 1);
            } else {
              i++;
            }
          });
      }
      if (request.method == 'PUT') {
        await this.requestProvider
          .put(request.url, request.data)
          .then((data) => {
            if (data == undefined) {
            }
            result.splice(i, 1);
          })
          .catch((err) => {
            this.logger.error(err, "ERREUR lors du renvoi d'une requete PUT");
            i++;
          });
      }
      if (request.method == 'DELETE') {
        await this.requestProvider
          .delete(request.url)
          .then((data) => {
            if (data == undefined) {
            }
            result.splice(i, 1);
          })
          .catch((err) => {
            this.logger.error(err, "ERREUR lors du renvoi d'une requete DELETE");
            i++;
          });
      }
    }

    await this.offlineSyncProvider
      .setRequests(result)
      .then((data) => {
        return data;
      })
      .catch((err) => {
        this.logger.error(err, `Error while OfflineSyncProvider#loopRequest`);
      });
  }

  /**
   * Arret du processus d'éxecution des requetes hors-ligne
   */
  stopRequests() {
    return new Promise((resolve) => {
      this.in_progress = false;
      resolve('data');
    });
  }
}
